import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeAnalyticsPostsByDate.graphql';
import { Query, QueryyoutubeAnalyticsPostsByDateArgs } from '../../__generated__/globalTypes';

export const useYoutubeAnalyticsPostsByDateQuery = (
  options?: QueryHookOptions<Query, QueryyoutubeAnalyticsPostsByDateArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeAnalyticsPostsByDateArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeAnalyticsPostsByDateLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeAnalyticsPostsByDateArgs>
): QueryResult<Query, QueryyoutubeAnalyticsPostsByDateArgs> & {
  getYoutubeAnalyticsPostsByDate: LazyQueryExecFunction<Query, QueryyoutubeAnalyticsPostsByDateArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeAnalyticsPostsByDate, result] = useLazyQuery<Query, QueryyoutubeAnalyticsPostsByDateArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getYoutubeAnalyticsPostsByDate, ...result };
};

export const useYoutubeAnalyticsPostsByDatePromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeAnalyticsPostsByDate = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeAnalyticsPostsByDateArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeAnalyticsPostsByDateArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeAnalyticsPostsByDate };
};
// Suspense Query Hooks
export const useYoutubeAnalyticsPostsByDateSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeAnalyticsPostsByDateArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeAnalyticsPostsByDateArgs>(QUERY, {
    ...options
  });

  return result;
};
